import { Component, ChangeDetectionStrategy, OnDestroy, inject } from '@angular/core';
import { CommonModule, DatePipe, NgIf } from '@angular/common';
import { combineLatest, map, Subscription } from 'rxjs';
import { ErrorService } from 'src/app/core/errors/error.service';
import { ApiError } from 'src/app/core/errors/api-error.model';
import { ModalLayoutService, ModalSize } from 'features/src/lib/donations/services/modal-layout.service';

type VM = { err: ApiError | null; size: ModalSize };

@Component({
    selector: 'app-global-error-modal',
    templateUrl: './error-modal.component.html',
    styleUrls: ['./error-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, NgIf, DatePipe]
})
export class ErrorModalComponent implements OnDestroy {
    private readonly errors = inject(ErrorService);
    private readonly layout = inject(ModalLayoutService);
    private readonly key = 'error-modal';
    private sub?: Subscription; 
    readonly supportEmail = 'info@crezcofoundation.org';
    
    vm$ = combineLatest([this.errors.state$, this.layout.size$]).pipe(
        map(([err, size]) => ({ err, size } as VM))
    );

    copied: Record<'correlationId' | 'traceId' | 'details', boolean> = {
        correlationId: false,
        traceId: false,
        details: false
    };
    private copyTimers: Record<'correlationId' | 'traceId' | 'details', any> = {
        correlationId: null,
        traceId: null,
        details: null
    };

    constructor() {
        this.sub = this.errors.state$.subscribe(err => {
            if (err) {
                this.layout.setSize(this.key, 'wide');
                if (typeof document !== 'undefined') document.body.style.overflow = 'hidden';
            } else {
                this.layout.clear(this.key);
                if (typeof document !== 'undefined') document.body.style.overflow = '';
            }
        });
    }

    ngOnDestroy(): void {
        this.layout.clear(this.key);
        this.sub?.unsubscribe();
        if (typeof document !== 'undefined') document.body.style.overflow = '';
        Object.values(this.copyTimers).forEach(t => t && clearTimeout(t));
    }

    close() { this.errors.clear(); }

    short(id: string) {
        return id && id.length > 20 ? `${id.slice(0, 8)}…${id.slice(-6)}` : id;
    }

    async copy(kind: 'correlationId' | 'traceId' | 'details', text: string) {
        try { await navigator.clipboard.writeText(text); } catch { }
        this.copied[kind] = true;
        if (this.copyTimers[kind]) clearTimeout(this.copyTimers[kind]);
        this.copyTimers[kind] = setTimeout(() => (this.copied[kind] = false), 1200);
    }

    buildDetailsText(err: ApiError): string {
        const obj = {
            time: err.time,
            status: err.status,
            url: err.url,
            code: err.code,
            message: err.message,
            correlationId: err.correlationId,
            traceId: err.traceId,
            details: err.details ?? null
        };
        return JSON.stringify(obj, null, 2);
    }
}
